import { z } from 'zod';

/**
 * General building blocks for form schemas. A form composes its fields from
 * these and Zod's own `.max()` / `.refine()`, so the same mistake reads the
 * same on every form:
 *
 *   const contactSchema = z.object({
 *     fullName: required('Full name').max(160, tooLong(160)),
 *     email,
 *     phone: optional(phone),
 *   });
 *
 * Fields that are the same everywhere — `email`, `phone` — are complete here,
 * and a form just calls them. Anything else is built from `required(label)`,
 * whose label names the field in its message — "Full name is required." — so
 * the error still makes sense read on its own, as a screen reader announces it.
 *
 * The limits a form passes in mirror the API's request schema. The API stays
 * the authority — these exist so a person hears about a mistake before sending,
 * in their words rather than the server's.
 */

/**
 * Text that must be filled in. Checked first and alone: an empty field says
 * only that it is required — `abort` stops the format checks chained after it
 * from piling "not a valid email" on top of an empty box.
 */
export const required = (label: string) =>
  z.string().trim().min(1, { message: `${label} is required.`, abort: true });

/**
 * Any field made optional: blank (or only spaces) is accepted as `''`, and
 * anything typed must pass that field's own rules — and comes out the way that
 * field puts it, so `optional(phone)` still sends `+961…`.
 */
export const optional = (field: z.ZodType<string, string>) =>
  z
    .string()
    .trim()
    .transform((value, ctx) => {
      if (value === '') return '';
      const result = field.safeParse(value);
      if (result.success) return result.data;
      for (const issue of result.error.issues) ctx.addIssue({ code: 'custom', message: issue.message });
      return z.NEVER;
    });

/** The message for `.max()`. */
export const tooLong = (max: number) => `Keep it to ${max.toLocaleString()} characters or fewer.`;

/** For `.refine()`: a well-formed email address. */
export const isEmail = (value: string) => z.email().safeParse(value).success;
export const EMAIL = 'Enter a valid email address, like name@example.com.';

/**
 * Every email field, complete: "Email is required." first, then the format.
 * 254 is the longest an address can be. Forms call it; they do not rebuild it.
 */
export const email = required('Email').max(254, tooLong(254)).refine(isEmail, EMAIL);

/**
 * What follows +961 in a Lebanese number, without the `0` used inside the
 * country: 8 digits for the newer mobile ranges (70, 71, 76, 78, 79, 81), and
 * 7 for `03` mobiles and landlines (01 Beirut, 04–09). A strict 8 would turn
 * away every `03` mobile and every landline.
 *
 * The 7-digit part leaves out the starts the mobile ranges own, so a mobile
 * typed one digit short ("70 123 45") is caught rather than read as a South
 * or Bekaa landline.
 */
export const LEBANESE_NUMBER = /^(?:(?:7[016-9]|81)\d{6}|(?:[1-69]\d|7[2-57]|8[02-9])\d{5})$/;
export const PHONE = 'Enter a Lebanese number, like 70 123 456 or 03 123 456.';

/**
 * The digits after +961, however the number was typed: spaces, dashes, dots
 * and brackets dropped, a pasted `+961` / `00961` taken off, and the local
 * `0` removed — so "03-123 456", "+961 3 123456" and "3123456" all agree.
 */
const lebaneseDigits = (value: string) =>
  value
    .replace(/[\s\-.()]/g, '')
    .replace(/^(?:\+|00)961/, '')
    .replace(/^0/, '');

/**
 * Every phone field, complete: Lebanese numbers only, "Phone number is
 * required." first, then the number itself. Sent in one form — `+96170123456`
 * — so the admin app sees every number written the same way. The input shows
 * the +961 itself (`PhoneInput`); the visitor types the rest.
 */
export const phone = required('Phone number')
  .refine((value) => LEBANESE_NUMBER.test(lebaneseDigits(value)), PHONE)
  .transform((value) => `+961${lebaneseDigits(value)}`);

/** For `.refine()`: one line — a subject, a title — where a newline is refused. */
export const isOneLine = (value: string) => !/[\r\n]/.test(value);
export const ONE_LINE = 'Keep it to one line.';

/** A choice from a select, where the empty value means nothing was picked. */
export const picked = (message = 'Choose one.') => z.string().min(1, message);

/** A box that has to be ticked to go on: a policy, a set of terms. */
export const accepted = (message: string) => z.boolean().refine((value) => value, message);
